import React from "react";

import cx from "classnames";

import { getClosureFor } from "@kazoohr/helpers";

import { useDropdownMenuHandlers } from "../../helpers/dropdownMenuHandlers";
import { useUIDSeed } from "../../helpers/useUIDSeed";
import { THEME_COLORS } from "../../theme/themeTokens";
import { Button } from "../Button";
import { Icon, IconDictionary, SIZES as IconSizes } from "../Icon";
import { Popper } from "../Popper";

import styles from "./Dropdown.scss";
import { DropdownList } from "./DropdownList";
import { DropdownButtonSize, DropdownOptionType, DropdownSize } from "./types";

export interface DropdownProps<
  T extends string | number | boolean = string | number | boolean
> {
  /**
   * Trigger button label.
   */
  label?: string;
  /**
   * ID(s) for external labels to precede trigger button label.
   *
   * Provides a way for extra clarity of Dropdown component, but if the button trigger text is enough, feel free to leave off this prop.
   */
  ariaLabelledby?: string;
  /**
   * Size of the trigger button.
   */
  buttonSize?: DropdownSize;
  /**
   * String value which displays an SVG icon to the left of text (for caret options see `icon`)
   */
  buttonIcon?: IconDictionary;
  /**
   * Visually hides the trigger button label, replacing it with ellipsis.
   */
  hideLabel?: boolean;
  /**
   * String value which allows customization of the Icon's SVG image
   */
  icon?: IconDictionary;
  /**
   * Number value which allows customization of the Icon's SVG size
   */
  iconSize?: number;
  /**
   * Stroke variant for the trigger icon, matching Icon's variants. Use "thin"
   * to visually match link-variant Button icons (e.g. ExpansionPanel).
   */
  iconVariant?: "regular" | "filled" | "thin";
  /**
   * ID value used for the trigger button and menu. Auto generated if not provided. The trigger button ID is set to the passed in value, and the dropdown menu ID is set to `dropdown-menu-ID`
   */
  id?: string;
  /**
   * Function called when an option is selected.
   */
  onChange?: (option: DropdownOptionType<T>) => void;
  /**
   * Array of options as described below.
   */
  options: DropdownOptionType<T>[];
  /**
   * Label for `Selected` text.
   */
  selectedLabel?: string;
  /**
   * The currently selected option `value`.
   */
  selectedOptionValue?: string | number | boolean;
  /**
   * When true, the dropdown will remain open after an option is selected.
   */
  keepOpenOnSelect?: boolean;
  /**
   * Classname for the list container.
   */
  listContainerClassname?: string;
  /**
   * Data attribute for testing.
   */
  "data-testid"?: string;
  /**
   * Variant of the button.
   */
  buttonVariant?: DropdownButtonSize;
  /**
   * Classname for the dropdown container.
   */
  className?: string;
}

const defaultIconColor = THEME_COLORS.interactiveDefault;

/**
 * An accessible dropdown component.
 *
 * - Follows accessibility guidelines for dropdown ([collapsible listbox](https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-collapsible.html))
 * - The variants knob is turned off for storybook, but will remain as a prop in KW or elsewhere in Confetti. To avoid breaking changes, we force `SYSTEM` in the component. [ todo ] : remove the variant from all instances.
 */
// eslint-disable-next-line max-lines-per-function
export function Dropdown<T extends string | number | boolean>({
  ariaLabelledby,
  buttonSize = "default",
  buttonIcon,
  buttonVariant = "transparentDark",
  "data-testid": dataTestId,
  hideLabel,
  icon = "caret_dn",
  iconSize = IconSizes.ExtraSmall,
  iconVariant = "regular",
  id,
  label = "",
  onChange,
  options,
  selectedLabel,
  selectedOptionValue,
  className,
  keepOpenOnSelect = false,
  listContainerClassname,
  ...rest
}: DropdownProps<T>) {
  const generateUID = useUIDSeed(id, label, ariaLabelledby);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [isOpen, setOpen] = React.useState(false);

  const focusOnTrigger = () => {
    triggerRef.current?.focus();
  };

  const dropdownMenuHandlers = useDropdownMenuHandlers({
    closeMenu: getClosureFor(setOpen, false),
    focusOnTrigger,
    openMenu: getClosureFor(setOpen, true),
    options,
    toggleMenu: getClosureFor(setOpen, !isOpen),
  });

  const {
    highlightedIndex,
    triggerClickHandler,
    triggerKeyDownHandler,
    menuKeyDownHandler,
    menuMouseEnterHandler,
  } = dropdownMenuHandlers;

  const handleOptionSelect = (option: DropdownOptionType<T>) => {
    onChange?.(option);
    if (!keepOpenOnSelect) {
      setOpen(false);
    }
    focusOnTrigger();
  };

  const dropdownTriggerId = id || generateUID("dropdown-trigger");
  const dropdownTriggerLabelId = `inner-${dropdownTriggerId}-label`;
  const dropdownMenuId = id
    ? `dropdown-menu-${id}`
    : generateUID("dropdown-menu");

  const ariaLabelIds = [ariaLabelledby, dropdownTriggerLabelId]
    .filter((id) => id)
    .join(" ");

  const dropDownClass = cx(styles.dropdown, className, {
    [styles["dropdown-button"]]: buttonVariant == "secondary" && !buttonSize,
  });

  const iconColor =
    buttonVariant === "pill" || buttonVariant === "primary"
      ? "currentColor"
      : undefined;

  return (
    <div role="presentation" className={dropDownClass}>
      <Popper
        isOpen={isOpen}
        onSetOpen={setOpen}
        closeOnClickAway={true}
        popupElement={
          <DropdownList
            dropdownMenuId={dropdownMenuId}
            dropdownTriggerId={dropdownTriggerId}
            menuKeyDownHandler={menuKeyDownHandler}
            menuMouseEnterHandler={menuMouseEnterHandler}
            options={options}
            selectedOptionValue={selectedOptionValue}
            highlightedIndex={highlightedIndex}
            onOptionSelect={handleOptionSelect}
            selectedLabel={selectedLabel || "Selected"}
            isOpen={isOpen}
            listContainerClassname={listContainerClassname}
          />
        }
      >
        <Button
          {...rest}
          id={dropdownTriggerId}
          className={styles["dropdown-trigger"]}
          size={buttonSize}
          variant={buttonVariant}
          onClick={triggerClickHandler}
          innerRef={triggerRef}
          aria-haspopup="listbox"
          aria-controls={dropdownMenuId}
          aria-expanded={isOpen}
          aria-labelledby={ariaLabelIds}
          onKeyDown={triggerKeyDownHandler}
          data-testid={dataTestId || "dropdown-button"}
          icon={hideLabel ? undefined : buttonIcon}
        >
          {hideLabel ? (
            <>
              <span id={dropdownTriggerLabelId} className="screen-reader-only">
                {label}
              </span>
              <Icon
                icon="meatball_menu"
                variant="filled"
                className={styles["trigger-button-menu-icon"]}
                color={iconColor ?? defaultIconColor}
              />
            </>
          ) : (
            <>
              <span
                id={dropdownTriggerLabelId}
                className={styles["trigger-button-label"]}
              >
                {label}
              </span>
              {buttonIcon && (
                <Icon
                  icon={buttonIcon}
                  size={iconSize}
                  variant={iconVariant}
                  className={styles["trigger-button-icon"]}
                  color={iconColor}
                />
              )}
              {!buttonIcon && (
                <Icon
                  icon={icon}
                  size={iconSize}
                  variant={iconVariant}
                  className={styles["trigger-button-icon"]}
                  color={iconColor}
                />
              )}
            </>
          )}
        </Button>
      </Popper>
    </div>
  );
}
